/**
 * SnapshotPreview.tsx - Company Snapshot Report Preview Page
 *
 * Quick preview page to visualize Company Snapshot report data.
 * Accepts a ticker symbol and displays the report in a card-based layout.
 */

import { useState, useEffect, ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Activity,
  Users,
  Target,
  AlertCircle,
  Loader2,
  RefreshCw,
  PieChart,
  Percent,
  Scale,
} from 'lucide-react';
import { stocksAPI, CompanySnapshotResponse, MetricWithContext } from '../../services/api';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const MetricCard = ({
  label,
  metric,
  icon: Icon,
}: {
  label: string;
  metric: MetricWithContext;
  icon?: React.ElementType;
}) => {
  const getTrendColor = (trend: string | null | undefined) => {
    if (trend === 'improving') return 'text-green-600';
    if (trend === 'declining') return 'text-red-600';
    return 'text-gray-500';
  };

  const getTrendIcon = (trend: string | null | undefined) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4" />;
    if (trend === 'declining') return <TrendingDown className="w-4 h-4" />;
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-xl font-semibold text-gray-900 dark:text-white">
        {metric.formatted}
      </div>
      {metric.trend && (
        <div className={`flex items-center gap-1 mt-1 text-sm ${getTrendColor(metric.trend)}`}>
          {getTrendIcon(metric.trend)}
          <span className="capitalize">{metric.trend}</span>
        </div>
      )}
    </div>
  );
};

const PerformanceBar = ({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) => {
  if (value === null) return null;

  const isPositive = value >= 0;
  const absValue = Math.abs(value);
  const maxWidth = Math.min(absValue, 50); // Cap at 50% width

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500 dark:text-gray-400 w-12">{label}</span>
      <div className="flex-1 flex items-center">
        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 relative">
          <div
            className={`h-2 rounded-full ${isPositive ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${maxWidth}%`, marginLeft: isPositive ? '50%' : `${50 - maxWidth}%` }}
          />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 dark:bg-gray-600" />
        </div>
      </div>
      <span className={`text-sm font-medium w-16 text-right ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {value >= 0 ? '+' : ''}{value.toFixed(1)}%
      </span>
    </div>
  );
};

const ClassificationTag = ({ label, variant = 'default' }: { label: string; variant?: 'default' | 'primary' | 'success' | 'warning' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    primary: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    success: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${variants[variant]}`}>
      {label}
    </span>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SnapshotPreview(): ReactElement {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CompanySnapshotResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSnapshot = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);

    try {
      const response = await stocksAPI.getCompanySnapshot(symbol.toUpperCase());
      setData(response.data);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load snapshot';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnapshot();
  }, [symbol]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading snapshot for {symbol?.toUpperCase()}...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <AlertCircle className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">Error Loading Snapshot</h3>
                <p className="text-sm mt-1">{error || 'Unknown error'}</p>
              </div>
            </div>
            <button
              onClick={fetchSnapshot}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-800 rounded-lg text-red-700 dark:text-red-300 hover:bg-red-200"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { classification, price_performance } = data;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={fetchSnapshot}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Company Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {data.symbol}
                </h1>
                <span className="text-lg text-gray-500 dark:text-gray-400">
                  {data.company_name !== data.symbol ? data.company_name : ''}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <Building2 className="w-4 h-4" />
                <span>{classification.sector || 'N/A'}</span>
                <span className="text-gray-300">|</span>
                <span>{classification.industry || 'N/A'}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <ClassificationTag label={classification.market_cap_category} variant="primary" />
                <ClassificationTag label={classification.investment_style} />
                <ClassificationTag label={classification.quality_tier} variant={classification.quality_tier === 'Premium' ? 'success' : 'default'} />
                {classification.tags.map((tag) => (
                  <ClassificationTag key={tag} label={tag} variant="warning" />
                ))}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                ${price_performance.current_price.toFixed(2)}
              </div>
              <div className={`text-lg font-medium ${(price_performance.change_1d || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(price_performance.change_1d || 0) >= 0 ? '+' : ''}{price_performance.change_1d?.toFixed(2) || 0}% today
              </div>
            </div>
          </div>
        </div>

        {/* Price Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Price Performance
          </h2>
          <div className="space-y-3">
            <PerformanceBar label="1D" value={price_performance.change_1d} />
            <PerformanceBar label="1W" value={price_performance.change_1w} />
            <PerformanceBar label="1M" value={price_performance.change_1m} />
            <PerformanceBar label="3M" value={price_performance.change_3m} />
            <PerformanceBar label="YTD" value={price_performance.change_ytd} />
            <PerformanceBar label="1Y" value={price_performance.change_1y} />
          </div>
          <div className="flex gap-6 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm">
            <div>
              <span className="text-gray-500">52W High:</span>
              <span className="ml-2 font-medium">${price_performance.high_52w?.toFixed(2) || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">52W Low:</span>
              <span className="ml-2 font-medium">${price_performance.low_52w?.toFixed(2) || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">From High:</span>
              <span className={`ml-2 font-medium ${(price_performance.from_52w_high || 0) < 0 ? 'text-red-600' : ''}`}>
                {price_performance.from_52w_high?.toFixed(1) || 'N/A'}%
              </span>
            </div>
          </div>
        </div>

        {/* Key Financials */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Key Financials
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <MetricCard label="Revenue" metric={data.revenue} icon={DollarSign} />
            <MetricCard label="Net Income" metric={data.net_income} icon={DollarSign} />
            <MetricCard label="Gross Margin" metric={data.gross_margin} icon={Percent} />
            <MetricCard label="Operating Margin" metric={data.operating_margin} icon={Percent} />
            <MetricCard label="Net Margin" metric={data.net_margin} icon={Percent} />
          </div>
        </div>

        {/* Growth Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Growth Metrics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <MetricCard label="Revenue Growth (YoY)" metric={data.revenue_growth_yoy} icon={TrendingUp} />
            <MetricCard label="Earnings Growth (YoY)" metric={data.earnings_growth_yoy} icon={TrendingUp} />
            <MetricCard label="Revenue CAGR (3Y)" metric={data.revenue_cagr_3y} icon={BarChart3} />
          </div>
        </div>

        {/* Valuation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Valuation
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="P/E Ratio" metric={data.pe_ratio} />
            <MetricCard label="P/B Ratio" metric={data.pb_ratio} />
            <MetricCard label="P/S Ratio" metric={data.ps_ratio} />
            <MetricCard label="EV/EBITDA" metric={data.ev_ebitda} />
          </div>
        </div>

        {/* Quality Indicators */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Quality Indicators
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <MetricCard label="ROE" metric={data.roe} />
            <MetricCard label="ROA" metric={data.roa} />
            <MetricCard label="ROIC" metric={data.roic} />
            <MetricCard label="Debt/Equity" metric={data.debt_to_equity} />
            <MetricCard label="Current Ratio" metric={data.current_ratio} />
            <MetricCard label="Free Cash Flow" metric={data.free_cash_flow} icon={DollarSign} />
          </div>
        </div>

        {/* Ownership & Sentiment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Ownership
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Institutional Ownership</span>
                <span className="font-medium">
                  {data.institutional_ownership_pct?.toFixed(1) || 'N/A'}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Insider Sentiment</span>
                <span className={`font-medium ${
                  data.insider_buy_sell_ratio === 'Net Buying' ? 'text-green-600' :
                  data.insider_buy_sell_ratio === 'Net Selling' ? 'text-red-600' : ''
                }`}>
                  {data.insider_buy_sell_ratio || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Analyst
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Analyst Rating</span>
                <span className="font-medium">{data.analyst_rating || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Price Target</span>
                <span className="font-medium">
                  {data.price_target ? `$${data.price_target.toFixed(2)}` : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Upside</span>
                <span className={`font-medium ${(data.price_target_upside || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {data.price_target_upside ? `${data.price_target_upside.toFixed(1)}%` : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Data Quality */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Data Quality
          </h2>
          <div className="flex flex-wrap gap-6">
            <div>
              <span className="text-gray-500">Years of Data:</span>
              <span className="ml-2 font-medium">{data.years_of_data}</span>
            </div>
            {Object.entries(data.data_completeness).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="text-gray-500 capitalize">{key}:</span>
                <span className={`w-3 h-3 rounded-full ${value > 0 ? 'bg-green-500' : 'bg-red-500'}`} />
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Generated: {new Date(data.generated_at).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}
